/**
 * Hero — full-screen media "portal opens" entrance + scroll-away dissolve.
 *
 * Plain script (no bundler). Loaded with `defer` from the section, which sits
 * earlier in the document than the GSAP libraries at the end of <body>; defer
 * scripts run in document order, so we wait for GSAP to be ready before init.
 *
 * The destination of the Scene "portal" button. Same portal mechanic as the
 * Intro Portal / Scene: the media is full-screen behind a veil in the
 * background colour; the veil has an arch-shaped hole (CSS mask). On load:
 *   1. Scrolling locks briefly; the arch hole grows 0 → full screen while the
 *      media fades in and scales a touch, then the veil dissolves.
 *   2. The centred titles + body settle in; scrolling unlocks.
 * On scroll the media EVAPORATES (opacity + scale + blur) into the next
 * section's colour — the same hand-off the Scene and Intro Portal use.
 *
 * Respects prefers-reduced-motion: no lock / pin / scrub — the composed rest
 * state (veil open, media + text visible) is left as the CSS renders it.
 * Re-initialises cleanly on Shopify editor section reloads.
 */
(function () {
  var triggers = []; // ScrollTriggers we create (for editor teardown)
  var tweens = []; // timelines/tweens we create
  var cleanups = []; // misc listener removers

  function ready() {
    return window.gsap && window.ScrollTrigger;
  }

  function whenReady(cb) {
    if (ready()) return cb();
    // Poll until the GSAP libs (later in the document) have executed, so init
    // can never silently no-op if they aren't ready at DOMContentLoaded.
    var tries = 0;
    (function poll() {
      if (ready()) return cb();
      if (tries++ > 600) return; // ~10s safety cap
      requestAnimationFrame(poll);
    })();
  }

  function finalHole() {
    return Math.max(window.innerWidth, window.innerHeight) * 1.7;
  }

  // Fade the page's ambient track in as the portal opens — mirrors the landing
  // intro's playAudio (intro-portal.js). Best-effort: on a fresh navigation the
  // browser may block autoplay (no carried-over gesture), in which case the
  // corner sound bars stay paused and the visitor clicks to start. The .play()
  // promise rejection is swallowed inside audio.js, so a block is harmless.
  function playAudio() {
    if (!window.tamreAudio) return;
    var el = window.tamreAudio.el;
    el.volume = 0;
    window.tamreAudio.play();
    gsap.to(el, { volume: 0.7, duration: 3.5, ease: 'none' });
  }
  function startAudio() {
    if (window.tamreAudio) playAudio();
    else document.addEventListener('tamre:audio-ready', playAudio, { once: true });
  }

  // gsap.to() throws on a null target — filter optional content out.
  function present(list) {
    return list.filter(function (el) {
      return !!el;
    });
  }

  function reset() {
    triggers.forEach(function (t) {
      t.kill();
    });
    tweens.forEach(function (t) {
      t.kill();
    });
    cleanups.forEach(function (fn) {
      fn();
    });
    triggers = [];
    tweens = [];
    cleanups = [];
    if (window.lenis) window.lenis.start(); // never leave scroll locked
    document.querySelectorAll('[data-hero]').forEach(function (s) {
      s.__heroInit = false;
    });
  }

  function setup(section) {
    if (!section || section.__heroInit) return;
    section.__heroInit = true;

    // Blend this section's bottom into whatever follows: read the next section's
    // background colour (exposed as data-ui-bg) and fade toward it, so the
    // dissolve never meets a hard colour edge. Falls back to the flat --hero-bg.
    var wrap = section.closest('.shopify-section');
    var nextEl = wrap && wrap.nextElementSibling;
    var nextColored =
      nextEl && (nextEl.matches('[data-ui-bg]') ? nextEl : nextEl.querySelector('[data-ui-bg]'));
    var nextBg = nextColored && nextColored.getAttribute('data-ui-bg');
    if (nextBg) section.style.setProperty('--hero-fade-to', nextBg);

    var bg = section.querySelector('[data-bg]');
    var fade = section.querySelector('[data-fade]'); // next-section colour backdrop
    var veil = section.querySelector('[data-veil]');
    var content = present([
      section.querySelector('[data-h-script]'),
      section.querySelector('[data-h-sans]'),
      section.querySelector('[data-body]')
    ]);

    // Clear any hand-off flag left by the Scene portal button. The entrance
    // always plays; the flag only drives the Scene page's exit fade.
    try {
      sessionStorage.removeItem('tamre:portal');
    } catch (e) {}

    // "Static mode": no lock / pin / scrub. True under reduced-motion AND inside
    // the Shopify theme editor (Shopify.designMode), so editing shows the
    // composed rest state (veil open, media + text visible) instead of replaying.
    var staticMode =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !!(window.Shopify && window.Shopify.designMode);
    if (staticMode) return;

    // Initial (closed / hidden) state.
    gsap.set(veil, { '--hole': '0px', autoAlpha: 1 });
    gsap.set(bg, { opacity: 0, scale: 1 });
    // Titles + body focus in from a soft blur (blur(6px) → 0) as they rise, to
    // match the Interlude / Survey / Scene reveals so the sections feel cohesive.
    // Small text elements only (not the full-screen media), so cheap on mobile;
    // set past the staticMode return above, so reduced-motion / editor stay sharp.
    if (content.length) gsap.set(content, { y: 20, autoAlpha: 0, filter: 'blur(6px)' });

    // Lock scrolling at the top for the brief portal open, so the reveal isn't
    // scrolled through and the scroll-away pin can be built cleanly at scroll 0.
    window.scrollTo(0, 0);
    if (window.lenis) {
      window.lenis.scrollTo(0, { immediate: true });
      window.lenis.stop();
    }

    // Fade the ambient track in alongside the visual open (best-effort autoplay;
    // corner sound bars remain the manual fallback). Skipped in staticMode above.
    startAudio();

    // --- Portal open (autoplay): arch grows to full, media fades up, veil
    //     dissolves, titles settle in. Then unlock + build the scroll-away. ---
    var open = gsap.timeline({
      defaults: { ease: 'power2.out' },
      onComplete: function () {
        if (window.lenis) window.lenis.start();
        startDissolve();
      }
    });
    tweens.push(open);
    // Matched to the landing intro's "move" step-through (intro-portal.js): arch
    // grows over duration 4, veil fades at ~2.6 — so both entrances move at the
    // same slow, ethereal pace.
    open.to(veil, { '--hole': function () { return finalHole() + 'px'; }, ease: 'power1.inOut', duration: 4 }, 0);
    // sine.inOut (not the snappy default power2.out) so the media floats in
    // without a hard settle — reads calmer / more ethereal.
    open.to(bg, { opacity: 1, scale: 1.08, ease: 'sine.inOut', duration: 4 }, 0);
    // Fade the veil out so the arch's concave edges don't leave coloured wedges.
    open.to(veil, { autoAlpha: 0, duration: 1.2 }, 2.6);
    if (content.length) open.to(content, { y: 0, autoAlpha: 1, filter: 'blur(0px)', duration: 1.5, stagger: 0.16 }, 2.8);

    // Pin the media and let it EVAPORATE in place on scroll — the whole frame
    // fades out with a faint scale-up + soft blur (like mist clearing) while the
    // next section's colour rises behind it, so the pin release is invisible.
    // Built after the open completes (like the Intro Portal) so the two don't
    // fight over the same targets at scroll 0.
    function startDissolve() {
      if (!bg) return;
      // Mobile shortens the pinned dissolve so the survey questions below come up
      // sooner (people don't scroll through a long hand-off and miss them). Desktop
      // keeps the tuned ethereal pace. Mirrors intro-portal.js's 64rem split.
      var isDesktop = window.matchMedia('(min-width: 64rem)').matches;
      var t = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: isDesktop ? '+=120%' : '+=85%',
          pin: true,
          scrub: 1,
          invalidateOnRefresh: true
        }
      });
      tweens.push(t);
      if (t.scrollTrigger) triggers.push(t.scrollTrigger);
      // scale starts at 1.08 (the open left it there); explicit `from` keeps the
      // scrub reversible.
      t.fromTo(
        bg,
        { opacity: 1, scale: 1.08, y: 0, filter: 'blur(0px)' },
        { opacity: 0, scale: 1.14, y: -80, filter: 'blur(8px)', ease: 'none', duration: 1 },
        0
      );
      // Raise the next section's colour behind the media exactly as it leaves.
      if (fade) t.fromTo(fade, { opacity: 0 }, { opacity: 1, ease: 'none', duration: 1 }, 0);
      // Fade the titles out early so they don't linger over the blur.
      if (content.length) t.fromTo(content, { autoAlpha: 1 }, { autoAlpha: 0, ease: 'none', duration: 0.4 }, 0);
      ScrollTrigger.refresh();
    }

    // NOTE: no manual resize → ScrollTrigger.refresh() here. A bare refresh()
    // forces a global _refreshAll that BYPASSES the ignoreMobileResize guard set
    // in animations.js, so on iOS every address-bar show/hide would re-measure
    // this pin and snap the scrub. ScrollTrigger's own built-in resize listener
    // already refreshes on real resizes / orientation changes.
  }

  function initAll() {
    gsap.registerPlugin(ScrollTrigger);
    document.querySelectorAll('[data-hero]').forEach(setup);
  }

  whenReady(initAll);

  // Recalculate once images/fonts have settled.
  window.addEventListener('load', function () {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });

  // Shopify theme editor: tear down + rebuild when the section reloads, so
  // changing a setting never leaves the reveal stuck at its hidden state.
  document.addEventListener('shopify:section:load', function (e) {
    if (!ready()) return;
    if (!e.target.querySelector('[data-hero]')) return;
    reset();
    document.querySelectorAll('[data-hero]').forEach(setup);
  });
})();
